import { useMemo, useState } from "react";
import { ArrowLeft, ArrowUpRight, Check, LayoutGrid, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { LAYOUTS, type LayoutArchetype } from "@dynamic-landing/layout";
import { Button, Card } from "@/ui";
import { cn } from "@/lib/cn";
import { THEME_PRESETS, type ThemePreset } from "@/editor/theme/themePresets";
import { MANUAL_THEME_PRESETS } from "@/editor/theme/themePresetsManual";

/**
 * Layouts — the gallery of layout ARCHETYPES, a sibling of Designs. Where
 * Designs changes a page's *look* (tokens), Layouts changes its *structure*:
 * picking one restructures the active page (via a recipe) AND — unless you opt
 * out — applies the paired DESIGN so the layout reads correctly (e.g. bento ↔
 * the Bento design). Apply needs a live page (the tree lives in Make's
 * useBuilder), so a card deep-links into Make and applies there, undoably.
 */

// Resolve a preset id → its display label (presets live in studio-ui; the
// layout catalog only references them by id). Manual presets win on name dedupe.
const PRESET_BY_ID: Record<string, ThemePreset> = (() => {
  const byId: Record<string, ThemePreset> = {};
  for (const p of [...THEME_PRESETS, ...MANUAL_THEME_PRESETS]) byId[p.id] = p;
  return byId;
})();
const presetLabel = (id: string) => PRESET_BY_ID[id]?.name ?? id;
const presetColors = (id: string) => PRESET_BY_ID[id]?.tokens.colors;

export function LayoutsScreen() {
  const nav = useNavigate();
  // The paired-design opt-out (brief §3): ON by default — picking a layout also
  // applies its recommended design so it lays out correctly.
  const [applyTokens, setApplyTokens] = useState(true);

  const apply = (layout: LayoutArchetype) => {
    nav("/make", {
      state: {
        layoutId: layout.id,
        recipeId: layout.recipeId,
        presetId: layout.recommendedPresetId,
        applyTokens,
      },
    });
  };

  return (
    <div className="stagger space-y-6 pb-4">
      <button
        type="button"
        onClick={() => nav("/make")}
        className="inline-flex items-center gap-1.5 self-start rounded-btn px-2 py-1 text-[13px] font-medium text-muted transition-colors hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
        Back to Make
      </button>

      <header className="dl-mesh-bg flex flex-wrap items-end justify-between gap-3 overflow-hidden rounded-card border border-hairline p-5">
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-full bg-paper/80 text-accent backdrop-blur-sm">
            <LayoutGrid className="h-5 w-5" strokeWidth={1.75} />
          </span>
          <div>
            <p className="eyebrow mb-1">Layouts</p>
            <h1 className="text-[24px] font-semibold tracking-tight text-ink-strong md:text-[28px]">
              Restructure, instantly.
            </h1>
          </div>
        </div>
        <button
          type="button"
          onClick={() => nav("/designs")}
          className="inline-flex items-center gap-1.5 self-start rounded-full border border-hairline bg-paper/70 px-3 py-1.5 text-[12px] font-medium text-muted backdrop-blur-sm transition-colors hover:border-ink/25 hover:text-ink"
        >
          Edit colors &amp; type in Designs
          <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
        </button>
      </header>

      {/* paired-design toggle (the opt-out) */}
      <Card className="flex flex-wrap items-center justify-between gap-3 p-3.5">
        <div className="flex items-start gap-2.5">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-accent" strokeWidth={1.75} />
          <div>
            <p className="text-[13px] font-medium text-ink-strong">Preview the paired design too</p>
            <p className="text-[12px] leading-relaxed text-muted">
              Each layout comes with a matching design. We'll pre-select it in the Design tab and
              preview it on the canvas so the layout reads right — apply it there to keep it. Turn
              this off to keep your current design and only change the structure.
            </p>
          </div>
        </div>
        <Toggle on={applyTokens} onChange={setApplyTokens} label="Preview paired design" />
      </Card>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {LAYOUTS.map((l) => (
          <LayoutCard key={l.id} layout={l} applyTokens={applyTokens} onApply={() => apply(l)} />
        ))}
      </div>

      <p className="text-[12px] leading-relaxed text-subtle">
        Applying a layout restructures the page you're editing in Make. It's fully undoable (the
        Undo button or ⌘Z), and nothing goes live until you Publish.
      </p>
    </div>
  );
}

/* -------------------------------- card ------------------------------------ */
function LayoutCard({
  layout,
  applyTokens,
  onApply,
}: {
  layout: LayoutArchetype;
  applyTokens: boolean;
  onApply: () => void;
}) {
  const pairedId = layout.recommendedPresetId;
  const colors = useMemo(() => presetColors(pairedId), [pairedId]);
  return (
    <Card className="group flex flex-col gap-3 p-4 transition-all duration-300 animate-in fade-in slide-in-from-bottom-2 hover:-translate-y-0.5 hover:border-ink/20 motion-reduce:animate-none">
      <LayoutThumb recipeId={layout.recipeId} colors={colors} />
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-[15px] font-semibold tracking-tight text-ink-strong">{layout.label}</h3>
          <p className="mt-0.5 text-[12.5px] leading-relaxed text-muted">{layout.description}</p>
        </div>
      </div>
      <p className="text-[11.5px] leading-relaxed text-subtle">{layout.structure}</p>

      <div className="mt-auto flex flex-wrap items-center gap-1.5">
        {layout.tags.slice(0, 3).map((t) => (
          <span key={t} className="rounded-chip bg-paper-2 px-2 py-0.5 text-[10.5px] font-medium text-muted dark:bg-white/5">
            {t}
          </span>
        ))}
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-hairline pt-3">
        <span className="inline-flex items-center gap-1.5 text-[11.5px] text-muted" title="The design paired with this layout">
          <Swatch colors={colors} />
          {applyTokens ? (
            <>Pairs with <span className="font-medium text-ink">{presetLabel(pairedId)}</span></>
          ) : (
            <>Keeps your current design</>
          )}
        </span>
        <Button variant="ink" size="sm" onClick={onApply}>
          <LayoutGrid className="h-3.5 w-3.5" strokeWidth={1.75} />
          Apply
        </Button>
      </div>
    </Card>
  );
}

/* --------------------------- structural thumbnail ------------------------- */
/** A tiny CSS mock of the archetype's structure so the gallery reads at a glance. */
function LayoutThumb({ recipeId, colors }: { recipeId: string; colors?: ThemePreset["tokens"]["colors"] }) {
  const surface = colors?.surface ?? "var(--c-paper-2)";
  const block = colors?.brand ?? "var(--c-ink-600)";
  const soft = colors?.contrast ?? "var(--c-subtle)";
  const cell = (key: string, style: React.CSSProperties) => (
    <div key={key} className="rounded-[3px]" style={{ backgroundColor: block, opacity: 0.85, ...style }} />
  );
  let inner: React.ReactNode;
  switch (recipeId) {
    case "bento":
      inner = (
        <div className="grid h-full grid-cols-4 grid-rows-2 gap-1">
          <div className="col-span-2 row-span-2 rounded-[3px]" style={{ backgroundColor: block }} />
          {cell("b1", { gridColumn: "span 2" })}
          {cell("b2", {})}
          {cell("b3", {})}
        </div>
      );
      break;
    case "editorial":
      inner = (
        <div className="flex h-full flex-col gap-1">
          <div className="h-3 rounded-[3px]" style={{ backgroundColor: block }} />
          <div className="grid flex-1 grid-cols-2 gap-1">
            {cell("e1", {})}
            {cell("e2", {})}
          </div>
          <div className="h-2 rounded-[3px]" style={{ backgroundColor: soft, opacity: 0.5 }} />
        </div>
      );
      break;
    case "feature-grid":
      inner = (
        <div className="flex h-full flex-col gap-1">
          <div className="h-4 rounded-[3px]" style={{ backgroundColor: block }} />
          <div className="grid flex-1 grid-cols-3 gap-1">
            {cell("f1", {})}
            {cell("f2", {})}
            {cell("f3", {})}
          </div>
        </div>
      );
      break;
    case "brutalist":
      inner = (
        <div className="flex h-full flex-col">
          <div className="flex-1" style={{ backgroundColor: block }} />
          <div className="flex-1" style={{ backgroundColor: soft, opacity: 0.6 }} />
          <div className="flex-1" style={{ backgroundColor: block, opacity: 0.85 }} />
        </div>
      );
      break;
    default: // stack
      inner = (
        <div className="flex h-full flex-col gap-1.5">
          <div className="h-4 rounded-[3px]" style={{ backgroundColor: block }} />
          {cell("s1", { height: "0.6rem" })}
          {cell("s2", { height: "0.6rem", opacity: 0.5 })}
        </div>
      );
  }
  return (
    <div className="h-24 w-full overflow-hidden rounded-card border border-hairline p-2" style={{ backgroundColor: surface }}>
      {inner}
    </div>
  );
}

/* ------------------------------- bits ------------------------------------- */
function Swatch({ colors }: { colors?: ThemePreset["tokens"]["colors"] }) {
  const c = colors ?? {};
  return (
    <span className="inline-flex overflow-hidden rounded-[3px] border border-black/10">
      {[c.surface ?? "#fff", c.brand ?? "#000", c.contrast ?? "#000"].map((col, i) => (
        <span key={i} className="h-3 w-1.5" style={{ backgroundColor: col }} />
      ))}
    </span>
  );
}

function Toggle({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={() => onChange(!on)}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
        on ? "bg-accent" : "bg-paper-2 dark:bg-white/10",
      )}
    >
      <span
        className={cn(
          "inline-flex h-5 w-5 translate-x-0.5 items-center justify-center rounded-full bg-white shadow transition-transform",
          on && "translate-x-[22px]",
        )}
      >
        {on && <Check className="h-3 w-3 text-accent" strokeWidth={3} />}
      </span>
    </button>
  );
}
